import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { usePermissions } from './hooks/usePermissions';
import Index from './pages/Index';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Users from './pages/Users';
import Assessments from './pages/Assessments';
import AssessmentExchanges from './pages/AssessmentExchanges';
import AssessmentItemExchanges from './pages/AssessmentItemExchanges';
import Persons from './pages/Persons';
import PersonHealthProfiles from './pages/PersonHealthProfiles';
import RiskCalculations from './pages/RiskCalculations';
import FlowClinical from './pages/Flow/FlowClinical';
import ClinicalSessions from './pages/ClinicalSessions';
import AppLayout from './components/Layout/AppLayout';

function LoadingScreen() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background">
      <Loader2 className="h-12 w-12 animate-spin text-primary" />
      <p className="mt-4 text-lg text-muted-foreground">Se încarcă...</p>
    </div>
  );
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  // If we have a user, show the content
  if (user) {
    return <>{children}</>;
  }

  // If still loading and no user yet, show loading screen
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Not loading and no user = not authenticated
  return <Navigate to="/login" replace />;
}

function ProtectedRoutes() {
  const { isAdminOrMedic } = usePermissions();

  return (
    <AppLayout>
      <Routes>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="profile" element={<Profile />} />
        {/* FlowClinical: All authenticated users can access */}
        <Route path="flow-clinical" element={<FlowClinical />} />
        {/* ClinicalSessions: Only Admin/Medic can access */}
        <Route path="clinical-sessions" element={isAdminOrMedic() ? <ClinicalSessions /> : <Navigate to="/dashboard" replace />} />
        {/* Users: Only Admin/Medic can access */}
        <Route path="users" element={isAdminOrMedic() ? <Users /> : <Navigate to="/dashboard" replace />} />
        {/* Assessments: Only Admin/Medic can access */}
        <Route path="assessments" element={isAdminOrMedic() ? <Assessments /> : <Navigate to="/dashboard" replace />} />
        {/* AssessmentExchanges: Only Admin/Medic can access */}
        <Route path="assessment-exchanges" element={isAdminOrMedic() ? <AssessmentExchanges /> : <Navigate to="/dashboard" replace />} />
        {/* AssessmentItemExchanges: Only Admin/Medic can access */}
        <Route path="assessment-item-exchanges" element={isAdminOrMedic() ? <AssessmentItemExchanges /> : <Navigate to="/dashboard" replace />} />
        {/* Persons: Only Admin/Medic can access */}
        <Route path="persons" element={isAdminOrMedic() ? <Persons /> : <Navigate to="/dashboard" replace />} />
        {/* PersonHealthProfiles: All authenticated users can access */}
        <Route path="person-health-profiles" element={<PersonHealthProfiles />} />
        {/* RiskCalculations: Only Admin/Medic can access */}
        <Route path="risk-calculations" element={isAdminOrMedic() ? <RiskCalculations /> : <Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AppLayout>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <ProtectedRoutes />
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;

